import LogoIcon from "../../assets/icons/LogoIcon";
import HouseIcon from "../../assets/icons/HouseIcon";
import CalendarSidebarIcon from "../../assets/icons/CalenarSidebarIcon";
import UserSidebarIcon from "../../assets/icons/UserSidebarIcon";
import FolderIcon from "../../assets/icons/FolderIcon";
import ExitIcon from "../../assets/icons/ExitIcon";
import MenuItem from "./MenuItem";
import UserIcon from "../../assets/icons/UserIcon";
import FolderPlusIcon from "../../assets/icons/FolderPlusIcon";
import api from "../../services/api";
import OccurrenceIcon from "../../assets/icons/OccurrenceIcon";
import CarlaoAnalyticsIcon from "../../assets/icons/CarlaoAnalyticsIcon";
import FlowSideBarIcon from "../../assets/icons/FlowSideBarIcon";
import AtendimentoSideBarIcon from "../../assets/icons/AtendimentoSideBarIcon";
import SlaSidebarIcon from "../../assets/icons/SlaSidebarIcon";
import PennFill from "../../assets/icons/PenFill";
import Button from "../Button";
import {
  AsideContainer,
  AsideToggle,
  Container,
  Content,
  HeaderContainer,
  HeaderHospitalActive,
  HeaderLogo,
  HeaderRightGroup,
  HeaderUser,
  MobileToggleButton,
} from "./style";

import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation } from "react-router";
import { type AsideItem, type IReport } from "./types";
import { useAuth } from "../../contexts/Auth/useAuth";
import { useActiveHospital } from "../../contexts/ActiveHospital/useActiveHospital";
import { useQuery } from "@tanstack/react-query";
import { useReportModal } from "../../contexts/ReportModal/useReportModal";
import {
  PERMISSION_ADMINISTRATOR,
  PERMISSION_REPORTS,
  PERMISSION_RESULT,
  PERMISSION_SCHEDULING,
} from "../../types/permissions";
import {
  ROLE_CONVENIO,
  ROLE_DOCTOR,
  ROLE_HOSPITALS,
  ROLE_OPERATION,
  ROLE_PATIENT,
} from "../../types/role";
import { useHospitalList } from "../../hooks/useHospitalList";
import { useActiveConvenio } from "../../contexts/ActiveConvenio/useActiveConvenio";
import { useAuthFeatureFlag } from "../../hooks/useAuthFeatureFlag";
import { useCarlaoAccess } from "../../hooks/useCarlaoAccess";

import FileMedical from "../../assets/icons/FileMedical";
import FileEarMarkIcon from "../../assets/icons/FileEarMarkIcon";
import ClipboardIcon from "../../assets/icons/ClipboardIcon";
import token_service from "../../services/token_service";
import loggedUserTitle from "./utils";
import { NotificationBell } from "../NotificationBell";

// Exported so the sidebar-gating rule for a per-user access answer (e.g.
// carlaoAccessAllowed below) can be unit-tested without rendering the whole
// Aside tree and its unrelated contexts (auth, hospital, reports, ...).
export const getAsideItems = (
  auth: any,
  handleOpenReportModal: () => void,
  reports: AsideItem[],
  _setChangeActiveHospitalIsOpen: Dispatch<SetStateAction<boolean>>,
  activeHospitalUuid?: string,
  agendamentoAmbulatorial?: boolean,
  carlaoAccessAllowed?: boolean,
): AsideItem[] => {
  const token = token_service.getAccessToken();
  const userId = auth.user.id;
  return [
    {
      content: auth.user?.role_id === ROLE_PATIENT ? "Resultado" : "Início",
      icon: auth.user?.role_id === ROLE_PATIENT ? <ClipboardIcon /> : <HouseIcon />,
      href:
        auth.user?.role_id === 5
          ? "/formulario-interno"
          : auth.user?.role_id === ROLE_PATIENT ||
            auth.user?.role_id === ROLE_DOCTOR
            ? "/resultados-exames"
            : "/dashboard",
    },
    {
      content: "Agendamento",
      href: `${import.meta.env.VITE_APP_SCHEDULE_URL}/login/sso?token=${token}&hospitalUuid=${activeHospitalUuid}&userId=${userId}&expired=${token_service.getTokenExpiry()}`,
      icon: <CalendarSidebarIcon />,
      permissions: [PERMISSION_SCHEDULING],
      role: (agendamentoAmbulatorial ? [ROLE_OPERATION, ROLE_PATIENT] : [ROLE_OPERATION]) as any,
      activeHospital: true,
    },
    {
      content: "Resultado",
      icon: <ClipboardIcon />,
      href: "/resultados",
      permissions: [PERMISSION_RESULT],
      role: [ROLE_OPERATION] as any,
    },
    {
      content: "Acréscimo",
      icon: <FileMedical />,
      href: "/acrescimo",
      role: [ROLE_OPERATION, ROLE_HOSPITALS, ROLE_DOCTOR] as any,
    },
    {
      content: "Documentos",
      href: `/documentos`,
      icon: <FileEarMarkIcon />,
      permissions: [12],
    },
    {
      content: "Assinaturas",
      href: `/doctors-templates`,
      icon: <PennFill />,
      permissions: [12],
    },
    {
      content: "Ocorrências",
      href: `/occurrence`,
      icon: <OccurrenceIcon />,
      role: ROLE_OPERATION,
      activeHospital: true,
    },
    {
      content: "Carlão — Painel",
      href: "/analises-carlao",
      icon: <CarlaoAnalyticsIcon />,
      // Gated on the backend self-check (GET /internal/carlao/analytics/access)
      // rather than on role/permissions alone: role_id === 1 is necessary but
      // not sufficient, the caller must also be on the Carlão dashboard
      // allowlist. `carlaoAccessAllowed` already resolves to `false` while the
      // check is loading or has failed (useCarlaoAccess mirrors
      // useAuthFeatureFlag), so `hidden` also covers the "no flicker"
      // requirement and fails closed on an error, not just on a denial.
      hidden: !carlaoAccessAllowed,
      permissions: [PERMISSION_ADMINISTRATOR],
      role: ROLE_OPERATION,
    },
    {
      content: "Usuários",
      href: "/users",
      icon: <UserSidebarIcon />,
      permissions: [PERMISSION_ADMINISTRATOR],
      role: ROLE_OPERATION,
      activeHospital: true,
    },
    {
      content: "Gestão Atendimento",
      href: `http://192.168.0.252/app/atendimento/login-65e9b29fc97e99357e01b5df?environment=production&token=${token}`,
      icon: <AtendimentoSideBarIcon />,
      role: ROLE_OPERATION,
      activeHospital: true,
    },
    {
      content: "NTO - Flow",
      href: `http://192.168.0.252/app/flow/login-65e5fdd79b7a3317810eb242?token=${token}`,
      icon: <FlowSideBarIcon />,
      role: ROLE_OPERATION,
      activeHospital: true,
    },
    {
      // Qualidade's SLA panel lives in SenneHub, an internal app outside this
      // portal. Unlike the two links above, no session token goes with it.
      content: "SLA",
      href: "https://senne-hub.vercel.app/",
      icon: <SlaSidebarIcon />,
      role: ROLE_OPERATION,
    },
    {
      content: "Novo Relatório",
      href: undefined,
      icon: <FolderPlusIcon />,
      classname: "--from_awesome",
      onClick: () => handleOpenReportModal(),
      role: ROLE_OPERATION,
      activeHospital: true,
    },
    {
      content: "Relatório",
      href: undefined,
      icon: <FolderIcon />,
      classname: "--from_awesome",
      permissions: [PERMISSION_REPORTS],
      dropdown: reports,
      role: ROLE_OPERATION,
      activeHospital: true,
    },
    {
      content: "Sair",
      href: "#exit",
      icon: <ExitIcon />,
      onClick: () => auth.logout(),
      classname: "--logout_bottom"
    },
  ];
};

// The default visibility rule applied once none of the special-cased roles
// above (ROLE_PATIENT's temporary Agendamento override, ROLE_CONVENIO, the
// null/inactive-hospital case) intercept first. Exported so a per-item gate
// like `hidden` can be tested against the real rule the sidebar applies,
// not a re-implementation of it.
export const isAsideItemVisibleForUser = (
  item: AsideItem,
  roleId: number | undefined,
  userPermissions: number[] | undefined,
): boolean => {
  if (item.hidden) return false;
  return (
    (!item.permissions && !item.role) ||
    !!item.permissions?.some((permission) => userPermissions?.includes(permission)) ||
    (Array.isArray(item.role) ? item.role : [item.role])?.includes(roleId as any)
  );
};

const formatReportToAside = (reports: IReport[]): AsideItem[] => {
  return reports.map((report) => ({
    content: report.label ?? report.name,
    href: `/relatorio/${report.id}`,
  }));
};

const Aside = ({ children, customPlaceholder }: { children: ReactNode; customPlaceholder?: ReactNode }) => {
  const activeHospital = useActiveHospital();
  const activeConvenio = useActiveConvenio();
  const reportModal = useReportModal();

  const auth = useAuth();
  const location = useLocation();

  const asideRef = useRef<HTMLElement>(null) as any;
  const listReportsRef = useRef<AbortController | null>(null);
  const mobileToggleRef = useRef<HTMLDivElement>(null);

  const [asideIsOpen, setAsideIsOpen] = useState(true);

  useHospitalList(Number(auth.user?.id), auth.user?.role_id !== ROLE_CONVENIO);

  const { enabled: agendamentoAmbulatorial } = useAuthFeatureFlag("agendamento_ambulatorial");
  // The self-check can only ever answer `true` for ROLE_OPERATION (role 1),
  // so every other role — patients, doctors, hospital and convenio users —
  // would otherwise issue this request on every session for no reason.
  const { allowed: carlaoAccessAllowed } = useCarlaoAccess(auth.user?.role_id === ROLE_OPERATION);

  const { data: reports } = useQuery({
    queryKey: [
      "reports",
      auth.user?.role_id === 1 ? { role_id: 1 } : { uuid: activeHospital.uuid },
    ],
    queryFn: async () => {
      listReportsRef.current?.abort();
      listReportsRef.current = new AbortController();
      try {
        const response = await api.get<{ reports: IReport[] }>("/report", {
          params: {
            uuid: activeHospital.uuid,
            userId: localStorage.getItem("User_id_senne"),
            permissions: activeHospital.permissions,
          },
          signal: listReportsRef.current.signal,
        });
        return response.data.reports;
      } catch (err) {
        console.error("API Error:", err);
        throw err;
      }
    },
    enabled: (!!activeHospital.uuid || auth.user?.role_id === ROLE_OPERATION) && auth.user?.role_id !== 3,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 6 * 60 * 60 * 1000,
  });

  const userPermissions = auth.user?.permissoes?.map((item: any) => {
    if (Number.isInteger(item)) return item;
    return Number(item.id);
  });

  const menuItems = useMemo(() => {
    const allMenuItems = getAsideItems(
      auth,
      () => reportModal.setUpdateReportModalIsOpen(true),
      formatReportToAside(reports ?? []),
      activeHospital.setChangeActiveHospitalIsOpen,
      activeHospital.uuid,
      agendamentoAmbulatorial,
      carlaoAccessAllowed,
    ).filter((item) => {
      // TEMPORÁRIO: Agendamento oculto para ROLE_PATIENT independente da feature flag.
      // Reverter para `!agendamentoAmbulatorial` quando a liberação for retomada.
      if (
        auth.user?.role_id === ROLE_PATIENT &&
        item.content === "Agendamento"
      ) {
        return false;
      }

      if (auth.user?.role_id === ROLE_CONVENIO) {
        if (item.classname === "--logout_bottom") return true; // Sair
        if (item.href === "/resultados") return true;
        if (item.href === "/users") return !!(item.permissions?.some((p) => userPermissions?.includes(p)));
        return false;
      }
      if (
        auth.user === null ||
        (auth.user?.role_id === ROLE_HOSPITALS &&
          activeHospital.isProcedenciaActive === false)
      ) {
        return item.content === "Início" || item.content === "Sair";
      }
      return isAsideItemVisibleForUser(item, auth.user?.role_id, userPermissions);
    });

    const logoutItem = allMenuItems.find((item) => item.classname === "--logout_bottom");
    const otherItems = allMenuItems.filter((item) => item.classname !== "--logout_bottom");

    return { otherItems, logoutItem };
  }, [
    auth,
    activeHospital.permissions,
    activeHospital.uuid,
    location.pathname,
    reportModal,
    reports,
    activeHospital.isProcedenciaActive,
    activeHospital.setChangeActiveHospitalIsOpen,
    userPermissions,
    agendamentoAmbulatorial,
    carlaoAccessAllowed,
  ]);

  useEffect(() => {
    const handleAsideClickOutside = (e: MouseEvent) => {
      if (
        window.innerWidth <= 768 &&
        asideRef.current &&
        mobileToggleRef.current &&
        !asideRef.current.contains(e.target as Node) &&
        !mobileToggleRef.current.contains(e.target as Node)
      ) {
        setAsideIsOpen(false);
      }
    };

    window.addEventListener("click", handleAsideClickOutside);

    return () => window.removeEventListener("click", handleAsideClickOutside);
  }, []);

  useEffect(() => {
    window.innerWidth <= 768 && setAsideIsOpen(false);
  }, [location.pathname]);

  // Páginas internas que se beneficiam de layout fullscreen — sem o aside global,
  // apenas um link "Voltar ao portal" no topo. Vazio desde que a revisão
  // laboratorial standalone, seu único consumidor, foi removida (a revisão
  // agora vive no modal de detalhe do acréscimo); o mecanismo fica para a
  // próxima página fullscreen, não para reintroduzir essa rota.
  const FULLSCREEN_PATHS: string[] = [];
  const isFullscreen = FULLSCREEN_PATHS.some((p) =>
    location.pathname.startsWith(p)
  );

  if (isFullscreen) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#FAF7F2",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 24px",
            background: "#fff",
            borderBottom: "1px solid #E5DDD0",
            position: "sticky",
            top: 0,
            zIndex: 50,
          }}
        >
          <Link
            to="/dashboard"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              color: "#B86A1E",
              fontWeight: 600,
              fontSize: 13.5,
              textDecoration: "none",
              padding: "6px 10px 6px 6px",
              borderRadius: 8,
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(243, 149, 63, 0.10)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Voltar ao portal
          </Link>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontSize: 13,
              color: "#1A1410",
            }}
          >
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                background: "rgba(91, 145, 95, 0.18)",
                color: "#3F7A40",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 11,
              }}
            >
              {(auth.user?.name ?? "?")
                .split(/\s+/)
                .filter(Boolean)
                .slice(0, 2)
                .map((n: string) => n[0])
                .join("")
                .toUpperCase() || "?"}
            </div>
            <div style={{ lineHeight: 1.15 }}>
              <div style={{ fontWeight: 600 }}>{auth.user?.name}</div>
              <div style={{ fontSize: 11, color: "rgba(48,48,48,0.6)" }}>
                {loggedUserTitle(auth.user?.role_id || 0)}
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
      </div>
    );
  }

  return (
    <Container>
      {/* Mobile toggle button — always visible */}
      <MobileToggleButton
        $isOpen={asideIsOpen}
        ref={mobileToggleRef}
        onClick={() => setAsideIsOpen((prev) => !prev)}
      >
        <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="25" height="25" viewBox="0 0 50 50">
          <path d="M 5 8 A 2.0002 2.0002 0 1 0 5 12 L 45 12 A 2.0002 2.0002 0 1 0 45 8 L 5 8 z M 5 23 A 2.0002 2.0002 0 1 0 5 27 L 45 27 A 2.0002 2.0002 0 1 0 45 23 L 5 23 z M 5 38 A 2.0002 2.0002 0 1 0 5 42 L 45 42 A 2.0002 2.0002 0 1 0 45 38 L 5 38 z"></path>
        </svg>
      </MobileToggleButton>

      <AsideContainer $isOpen={asideIsOpen} ref={asideRef}>
        <HeaderLogo $isOpen={asideIsOpen}>
          <div className="icon">
            <LogoIcon />
          </div>
          <AsideToggle
            $isOpen={asideIsOpen}
            onClick={() => setAsideIsOpen((prev) => !prev)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="25" height="25" viewBox="0 0 50 50">
              <path d="M 5 8 A 2.0002 2.0002 0 1 0 5 12 L 45 12 A 2.0002 2.0002 0 1 0 45 8 L 5 8 z M 5 23 A 2.0002 2.0002 0 1 0 5 27 L 45 27 A 2.0002 2.0002 0 1 0 45 23 L 5 23 z M 5 38 A 2.0002 2.0002 0 1 0 5 42 L 45 42 A 2.0002 2.0002 0 1 0 45 38 L 5 38 z"></path>
            </svg>
          </AsideToggle>
        </HeaderLogo>
        <MenuItem asideItems={menuItems.otherItems} pathname={location.pathname} />
        {customPlaceholder && asideIsOpen && customPlaceholder}
        {menuItems.logoutItem && <MenuItem asideItems={[menuItems.logoutItem]} pathname={location.pathname} />}
      </AsideContainer>

      <Content $isOpen={asideIsOpen}>
        <HeaderContainer>
          <HeaderUser>
            <div className="header_user_image">
              <UserIcon />
            </div>
            <div className="header_user_content">
              <div className="header_user_content_name">{auth.user?.name}</div>
              <div className="header_user_role">{loggedUserTitle(auth.user?.role_id || 0)}</div>
            </div>
          </HeaderUser>
          <HeaderRightGroup>
            {auth.user?.role_id !== ROLE_CONVENIO && <NotificationBell />}
            {auth.user?.role_id === ROLE_CONVENIO ? (
              <HeaderHospitalActive $noEdit>
                <span>{activeConvenio.ds_convenio || "Convênio"}</span>
              </HeaderHospitalActive>
            ) : (
              auth.user?.role_id !== ROLE_PATIENT &&
              auth.user?.role_id !== ROLE_DOCTOR &&
              auth.user?.status !== 5 &&
              auth.user?.role_id !== 5 && (
                <HeaderHospitalActive
                  onClick={() =>
                    auth.user?.status !== 5 && auth.user?.role_id !== 5
                      ? activeHospital.setChangeActiveHospitalIsOpen(true)
                      : null
                  }
                  $noEdit={auth.user?.status === 5 || auth.user?.role_id === 5}
                >
                  <span>
                    {activeHospital.name || "Nenhum hospital selecionado"}
                    <span className="icon" style={{ marginLeft: "5px" }}><PennFill /></span>
                  </span>
                </HeaderHospitalActive>
              )
            )}
          </HeaderRightGroup>
        </HeaderContainer>
        {children}
      </Content>
    </Container>
  );
};

export default Aside;
